'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import HeroImage from '@/public/globe-bg.jpg';
import { PrismicRichText } from '@prismicio/react';
import { asHTML, RichTextField } from '@prismicio/client';
import { PiCheckFatFill } from 'react-icons/pi';
import { ContactForm } from './ContactForm';
import { ContactDocument } from '@/prismicio-types';

// title fields are multi-paragraph but rendered as a single heading here, so
// strip the wrapping <p> — asHTML always emits one for a single-paragraph field.
const headingHtml = (field: RichTextField | undefined) =>
  (asHTML(field) || '').replace(/^<p>|<\/p>$/g, '');

interface ContactFormBulletsProps {
  className?: string;
  title?: RichTextField;
  description?: RichTextField;
  bullets?: RichTextField[];
  ctaFormTitle?: RichTextField;
  ctaFormDescription?: RichTextField;
  ctaFormFooter?: RichTextField;
  page: ContactDocument;
}

const ContactFormBullets = ({
  className,
  title,
  description,
  bullets = [],
  ctaFormTitle,
  ctaFormDescription,
  ctaFormFooter,
  page,
}: ContactFormBulletsProps) => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <section id="contact" className={`relative ${className ?? ''}`}>
      <Image
        src={HeroImage}
        alt="Hero background"
        fill
        sizes="100vw"
        quality={50}
        style={{ objectFit: 'cover', pointerEvents: 'none' }}
        className="pointer-events-none object-cover opacity-10 sm:opacity-50"
      />
      <div className="container relative sm:py-4 sm:pt-8">
        <div className="grid grid-cols-1 items-center gap-10 text-left md:grid-cols-[40%_57%] md:gap-8">
          <div>
            {title && (
              <h2
                className="mb-8 text-3xl font-normal leading-[1.05] text-gray-800 sm:text-4xl sm:font-light md:text-5xl [&_strong]:font-medium [&_strong]:text-blue-600"
                dangerouslySetInnerHTML={{ __html: headingHtml(title) }}
              />
            )}
            {description && (
              <div className="mb-6 max-w-xl text-base text-black sm:pr-6 font-normal sm:font-medium">
                <PrismicRichText field={description} />
              </div>
            )}
            {bullets.length > 0 && (
              <ul className="space-y-3">
                {bullets.map((bullet, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <PiCheckFatFill className="mt-1 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
                    <div className="text-base text-black font-normal sm:font-medium">
                      <PrismicRichText field={bullet} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="w-full max-w-2xl rounded-md bg-gray-100 p-6 md:justify-self-end">
            {!submitted && ctaFormTitle && (
              <h3
                className="mb-6 mt-2 text-xl font-semibold text-gray-800 leading-[1.2]"
                dangerouslySetInnerHTML={{ __html: headingHtml(ctaFormTitle) }}
              />
            )}
            {!submitted && ctaFormDescription && (
              <div className="mb-4 text-sm text-gray-600">
                <PrismicRichText field={ctaFormDescription} />
              </div>
            )}
            <ContactForm
              labels={page.data}
              hideCompany
              fullWidthButton
              onSubmitted={() => setSubmitted(true)}
            />
            {ctaFormFooter && (
              <div className="mt-5 text-center text-xs text-gray-500">
                <PrismicRichText field={ctaFormFooter} />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactFormBullets;
